import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ArrowLeft } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.user);
      toast.success(`Welcome back, ${data.user.name}`);
      const r = data.user.role;
      navigate(r === "sponsor" ? "/sponsor" : r === "vendor" ? "/vendor" : r === "state_coordinator" ? "/coordinator" : r === "finance" ? "/finance" : "/admin");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-emerald-950 tmf-grain flex flex-col">
      <div className="p-6"><button onClick={() => navigate("/")} className="text-emerald-200 flex items-center gap-2 text-sm"><ArrowLeft className="w-4 h-4" /> Back to Home</button></div>
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl tmf-rise">
          <Logo showTagline />
          <h1 className="mt-6 font-display font-bold text-2xl text-emerald-950">Portal Login</h1>
          <p className="mt-1 text-sm text-gray-600">For Sponsors, Vendors and TMF Administrators.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label>Email</Label>
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 h-12" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Password</Label>
                <button type="button" data-testid="forgot-password-link" onClick={() => navigate("/forgot-password")} className="text-xs text-emerald-700 font-semibold hover:underline">Forgot password?</button>
              </div>
              <PasswordInput testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 h-12" />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 rounded-full">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Login"}
            </Button>
          </form>
          <div className="mt-6 text-center text-sm text-gray-500 space-y-1">
            <p>New sponsor or vendor? <button onClick={() => navigate("/signup")} className="text-emerald-700 font-semibold">Create an account</button></p>
            <p>Are you a beneficiary? <button onClick={() => navigate("/check")} className="text-emerald-700 font-semibold">Check your benefit</button></p>
          </div>
        </div>
      </div>
    </div>
  );
}
